import React, { useState } from 'react';
import { workOrdersAPI, customerAPI } from '../../services/api';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import './workOrderModal.css';

// Common work-order categories — suggestions only (the field is free text).
const TYPE_SUGGESTIONS = ['Gutters', 'Chase Covers', 'Standing Seam', 'Roof Repair', 'Siding', 'Misc'];
const STATUSES = ['Open', 'Scheduled', 'In Progress', 'Complete', 'Cancelled'];

// Create OR edit a work order. Pass `workOrder` to edit an existing one.
// Pass `lockedProjectId` (e.g. when opened from a project) to pre-select and lock
// the job selector.
const WorkOrderModal = ({ isOpen, onClose, onSaved, projects = [], users = [], workOrder = null, lockedProjectId = null }) => {
  const editing = !!workOrder;

  React.useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    customerAPI.getAll({ type: 'Subcontractor', limit: 500 })
      .then((res) => {
        if (cancelled) return;
        setSubs((res.data || []).map((c) => ({
          id: c.id,
          displayName: c.displayName || c.companyName
            || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed',
          companyName: c.companyName,
          phone: c.phone || c.mobilePhone || c.mainPhone || c.workPhone,
        })));
      })
      .catch(() => { /* optional field — a failed load must not block the form */ });
    return () => { cancelled = true; };
  }, [isOpen]);
  const [projectId, setProjectId] = useState(workOrder?.projectId ?? lockedProjectId ?? '');
  const [title, setTitle] = useState(workOrder?.title ?? '');
  const [type, setType] = useState(workOrder?.type ?? '');
  const [status, setStatus] = useState(workOrder?.status ?? 'Open');
  const [description, setDescription] = useState(workOrder?.description ?? '');
  const [assignedToId, setAssignedToId] = useState(workOrder?.assignedToId ?? '');
  const [subcontractorId, setSubcontractorId] = useState(workOrder?.subcontractorId ?? '');
  // Subcontractors are contacts, not staff, and there are enough of them that a
  // dropdown would be a scroll. Loaded once when the modal opens rather than with
  // the rest of the page, since most work orders never touch this field.
  const [subs, setSubs] = useState([]);
  const [amount, setAmount] = useState(workOrder?.amount ?? '');
  const [scheduledDate, setScheduledDate] = useState(workOrder?.scheduledDate ? workOrder.scheduledDate.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const submit = async () => {
    if (!projectId) { setError('Choose the job this work order belongs to.'); return; }
    if (!title.trim()) { setError('Give the work order a title.'); return; }
    setSaving(true);
    setError('');
    const payload = {
      projectId: Number(projectId),
      title: title.trim(),
      type: type.trim() || null,
      status,
      description: description.trim() || null,
      assignedToId: assignedToId ? Number(assignedToId) : null,
      subcontractorId: subcontractorId ? Number(subcontractorId) : null,
      amount: amount ? Number(amount) : null,
      scheduledDate: scheduledDate || null,
    };
    try {
      const res = editing
        ? await workOrdersAPI.update(workOrder.id, payload)
        : await workOrdersAPI.create(payload);
      onSaved && onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the work order.');
      setSaving(false);
    }
  };

  return (
    // Closing is Cancel only — no click-to-close on the backdrop.
    <div className="wo-modal-overlay">
      <div className="wo-modal">
        <h3>{editing ? 'Edit Additional Work' : 'New Additional Work'}</h3>

        <label className="wo-label">Job *</label>
        <select className="wo-input" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!!lockedProjectId}>
          <option value="">Select a job…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.projectName}</option>
          ))}
        </select>

        <label className="wo-label">Title *</label>
        <input className="wo-input" value={title} autoFocus onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Replace back gutter run" />

        <div className="wo-row">
          <div className="wo-col">
            <label className="wo-label">Type</label>
            <input className="wo-input" list="wo-types" value={type} onChange={(e) => setType(e.target.value)}
              placeholder="Gutters, Chase Covers…" />
            <datalist id="wo-types">
              {TYPE_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
            </datalist>
          </div>
          <div className="wo-col">
            <label className="wo-label">Status</label>
            <select className="wo-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="wo-row">
          <div className="wo-col">
            <label className="wo-label">Amount</label>
            <input className="wo-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$" />
          </div>
          <div className="wo-col">
            <label className="wo-label">Assigned to</label>
            <select className="wo-input" value={assignedToId} onChange={(e) => setAssignedToId(e.target.value)}>
              <option value="">Unassigned</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="wo-row">
          <div className="wo-col wo-col-wide">
            <label className="wo-label">Subcontractor</label>
            {/* Searchable rather than a dropdown: staff are a short list, subs are
                not. Phone shown alongside, since whether one is reachable decides
                whether this work can actually be handed off. */}
            <SearchableSelect
              options={subs}
              value={subcontractorId}
              onChange={(e) => setSubcontractorId(e.target.value)}
              placeholder="Search subcontractors..."
              displayKey="displayName"
              valueKey="id"
              name="subcontractorId"
              detailKeys={['companyName', 'phone']}
            />
          </div>
        </div>

        <label className="wo-label">Scheduled</label>
        <input className="wo-input" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />

        <label className="wo-label">Description</label>
        <textarea className="wo-input wo-textarea" value={description} onChange={(e) => setDescription(e.target.value)}
          rows={3} placeholder="Scope / notes" />

        {error && <div className="wo-error">{error}</div>}

        <div className="wo-actions">
          <button className="wo-cancel" onClick={onClose}>Cancel</button>
          <button className="wo-save" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Work Order'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderModal;
